import React, { useCallback, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';

// Constants
import { billingStates } from 'Shared/constants/constants';
import PageUrlsConstants from 'Shared/constants/PageUrls.constants';

// Types
import { QuickStartGuideItemIDs } from '../QuickStartGuideItems.constants';

// Hooks
import { useToggle } from 'Shared/utils/hooks';
import useUpdateQuickStartGuideFlag from '../useUpdateQuickStartGuideFlag';

// Utils
import { trackClickQuickStartGuideCTA } from 'Shared/tracking/tracking';

// Components
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpButton, WpButtonGroup } from 'Wpds/components/button';
import AddGroupModalWithCalendarIntegration from 'Shared/components/modals/AddGroupModalWithCalendarIntegration/AddGroupModalWithCalendarIntegration';

// Styles
import './SetupGroupWorkspaceStep.scss';
import { Illustrations, WpIllustration } from 'Wpds/components/illustration';
import { useTranslation } from 'Shared/contexts/TranslationsContext';

type Props = {
  navigateToList: Function;
};

const SetupGroupWorkspaceStep = ({ navigateToList }: Props) => {
  const history = useHistory();
  const [isOpen, toggleIsOpen] = useToggle(false);

  const step = QuickStartGuideItemIDs.setupGroupWorkspace;

  const { handleSkipItem, hasBeenSet } = useUpdateQuickStartGuideFlag();
  const [isSet] = useState(hasBeenSet(step));
  const { tr } = useTranslation();

  const billingState = useSelector(state => state.organization.billingState);
  const isFreePlan = billingState === billingStates.FREE_PLAN;

  const handleSkipClick = () => {
    handleSkipItem(step);
    navigateToList();
  };

  const handleOnSuccess = () => {
    toggleIsOpen();
  };

  const handleCreateGroupWorkspaceClick = () => {
    trackClickQuickStartGuideCTA(step);
    toggleIsOpen();
  };

  const handleUpgradeClick = useCallback(() => {
    history.push({
      pathname: PageUrlsConstants.PLANS_PAGE,
      search: `?wp_content=quick_start_guide`,
      state: { modal: true },
    });
  }, [history]);

  useEffect(() => {
    if (!isSet && hasBeenSet(step)) navigateToList();
  }, [isSet, hasBeenSet]);

  const baseClassName = 'SetupGroupWorkspaceStep';
  return (
    <>
      <div className={baseClassName}>
        {isFreePlan && (
          <p className={`${baseClassName}__upgrade-msg`}>
            {tr('perform.onboarding.qsg.setup_group_workspace.upgrade_message')}
          </p>
        )}
        <div className={`${baseClassName}__button`}>
          <WpButtonGroup stacked>
            {isFreePlan ? (
              <WpButton size="lg" onClick={handleUpgradeClick}>
                <WpIcon name="crown" />
                {tr('perform.onboarding.qsg.setup_group_workspace.upgrade')}
              </WpButton>
            ) : (
              <WpButton size="lg" onClick={handleCreateGroupWorkspaceClick}>
                <WpIcon name="plus-circle" />
                {tr('perform.onboarding.qsg.setup_group_workspace.create')}
              </WpButton>
            )}
            {!isSet && (
              <WpButton size="lg" onClick={handleSkipClick} variant="tertiary">
                {tr('perform.onboarding.qsg.common.skip')}
              </WpButton>
            )}
          </WpButtonGroup>
        </div>
        <WpIllustration
          className={`${baseClassName}__image`}
          src={Illustrations.two_people_at_table_in_conference_call_purple_bg}
        />
      </div>
      {isOpen && <AddGroupModalWithCalendarIntegration onSuccess={handleOnSuccess} onClose={toggleIsOpen} />}
    </>
  );
};
export default SetupGroupWorkspaceStep;
